import { useState, useEffect, ChangeEvent, useCallback } from 'react';
import { connect } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import cn from 'classnames';

import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IDictionaryChecklistSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryChecklistSelectOption';
import { NEW_CHECKLIST_SELECT_OPTION_CODE } from 'KnmErknmPackage/MainModule/constants/newChecklistSelectOption';
import { IKnmErknmFormChecklist } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormChecklist';
import { IKnmErknmFormChecklistQuestion } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormChecklistQuestion';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getIsRsnCngPPXXII,
  getKnmDataCreated,
  getKnmErknmErvkChecklistsSelectOptions,
  getKnmIsEditEnabledByEditors,
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErpStatusTypeWaitingCarryOut,
  getKnmTypeIdFormValue,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { KnmChecklistDropdown } from 'KnmPackage/MainModule/components/KnmChecklistDropdown';
import { KnmErknmTypeIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmTypeIdsByCodeEnum';
import { getPlanListPlanErknmStatusTitle } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { ErknmPlanStatusTitlesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusTitlesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IErvkCheckList } from 'APIPackage/DictionariesModule/interfaces/IErvkCheckList';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './ErknmChecklist.module.scss';
import { ErknmFieldMark } from '../../../MainModule/components/ErknmFieldMark';
import { makeRequired } from '../../../../CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from '../../../../CorePackage/MainModule/constants/validationErrors';

const PAGE_SIZE = 10;

const getInitialItemsCheck = (questions: IKnmErknmFormChecklist['questions']) => {
  const initialItemsCheck: TableCheckItems = {};

  questions?.forEach(question => {
    if (question.listId) {
      initialItemsCheck[question.listId.toString()] = false;
    }
  });

  return initialItemsCheck;
};

interface IErknmChecklistProps {
  watchOnly?: boolean;
  formRef: IKnmFormRef | undefined;
  fieldName: string;
  data: IKnmErknmFormChecklist;
  checklistsSelectOptions: IDictionaryChecklistSelectOption[] | undefined;
  isSupervisor: boolean | undefined;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isKnmStatusWaitingCarriedOut: boolean;
  knmTypeIdFormValue: number | undefined;
  knmDataPlanStatus: string | null | undefined;
  ervkChecklistsSelectOptions: IDictionarySelectOption<IErvkCheckList>[] | undefined;
  settings: IAdminSetting[] | undefined;
  created: string | null | undefined;
  isKnmEditEnabledByEditors: boolean;
  isKnmStatusApproving: boolean;
  isRsnCngPPXXII: boolean;
}

export const ErknmChecklistWithoutProps = ({
  watchOnly,
  formRef,
  fieldName,
  data,
  checklistsSelectOptions,
  isSupervisor,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isKnmStatusWaitingCarriedOut,
  knmTypeIdFormValue,
  knmDataPlanStatus,
  ervkChecklistsSelectOptions,
  settings,
  created,
  isKnmEditEnabledByEditors,
  isKnmStatusApproving,
  isRsnCngPPXXII,
}: IErknmChecklistProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isKnmStatusAndTypeAvailableToAdding: boolean =
    KnmErknmTypeIdsByCodeEnum.PP === knmTypeIdFormValue &&
    (isKnmStatusTypeCarriedOut || isKnmStatusWaitingCarriedOut) &&
    knmDataPlanStatus === ErknmPlanStatusTitlesEnum.APPROVED;
  const isAddingAvailable: boolean = data.isNew || isKnmStatusAndTypeAvailableToAdding;
  const questionFieldName = `${fieldName}.questions` as keyof IKnmForm;
  const [questionsPage, setQuestionsPage] = useState<number>(0);
  const lastVisibleQuestionNumber = (questionsPage + 1) * PAGE_SIZE;
  const isNextPageAvailable = data.questions && data.questions.length > lastVisibleQuestionNumber;
  const isAnswerTypeFieldEnabled = isSupervisor && (isKnmStatusTypeCompleted || isKnmStatusTypeCarriedOut);
  const isNoteAnswerFieldEnabled = isAnswerTypeFieldEnabled;
  const handleLoadButtonClick = useCallback(() => setQuestionsPage(prev => prev + 1), []);
  const isNewChecklist = data.checkList?.code === NEW_CHECKLIST_SELECT_OPTION_CODE;

  const isErvkCheckListDisabled = (() => {
    if (isSupervisor) {
      if (isRsnCngPPXXII) {
        return false;
      }

      return (data.ervkCheckList && watchOnly) || isKnmStatusApproving;
    }

    return watchOnly;
  })();

  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>(getInitialItemsCheck(data.questions));
  const [isCheckboxAllChecked, setIsCheckboxAllChecked] = useState(false);

  useEffect(() => {
    setItemsCheck(getInitialItemsCheck(data.questions));
  }, [data.questions]);

  useEffect(() => {
    const keysArray = Object.keys(itemsCheck).filter(itemId => {
      const questionIndex = data.questions ? data.questions.findIndex(question => `${question.listId}` === itemId) : -1;

      return questionIndex >= 0 && questionIndex < lastVisibleQuestionNumber;
    });
    const isEachItemTrue = keysArray.length ? keysArray.every(key => itemsCheck[key]) : false;

    setIsCheckboxAllChecked(isEachItemTrue);
  }, [itemsCheck, data.questions, lastVisibleQuestionNumber]);

  const handleCheckboxAllChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { checked: isChecked } = e.target;

      setItemsCheck(prev => {
        const newItemsCheck: TableCheckItems = { ...prev };

        Object.keys(itemsCheck).forEach(itemId => {
          const questionIndex = data.questions
            ? data.questions.findIndex(question => `${question.listId}` === itemId)
            : -1;

          if (questionIndex >= 0 && questionIndex < lastVisibleQuestionNumber) {
            newItemsCheck[itemId] = isChecked;
          } else {
            newItemsCheck[itemId] = false;
          }
        });

        return newItemsCheck;
      });
    },
    [data.questions, itemsCheck, lastVisibleQuestionNumber],
  );

  const handleCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setItemsCheck({
        ...itemsCheck,
        [e.target.id]: e.target.checked,
      });
    },
    [itemsCheck],
  );

  const addQuestion = useCallback(() => {
    if (!watchOnly && push) {
      const newQuestionId = uuidv4();
      const newQuestion: IKnmErknmFormChecklistQuestion = {
        code: newQuestionId,
        listId: newQuestionId,
        isNew: true,
      };

      push(questionFieldName, newQuestion);
    }
  }, [push, questionFieldName, watchOnly]);

  const makeRemoveQuestion = (index: number) => () => !watchOnly && remove && remove(questionFieldName, index);

  const handleTitleSelectChange = useCallback(
    (value: IDictionaryChecklistSelectOption | undefined) => {
      const newTitleFieldName = `${fieldName}.newTitle` as keyof IKnmForm;
      const questionsFieldName = `${fieldName}.questions` as keyof IKnmForm;

      formRef?.current.change(newTitleFieldName);
      if (!value || value?.code === NEW_CHECKLIST_SELECT_OPTION_CODE) {
        formRef?.current.change(questionsFieldName);
      } else {
        const questions: IKnmErknmFormChecklist['questions'] = value?.questions?.map(question => {
          return {
            id: question.id,
            code: question.code || '',
            title: question.title || undefined,
            answer: undefined,
            listId: question.code || uuidv4(),
          };
        });

        formRef?.current.change(questionsFieldName, questions);
      }
    },
    [fieldName, formRef],
  );

  const isShow19 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_19_ERVK_CHECK_LISTS_STARTS_FROM,
  );

  return (
    <>
      <div className="shared-group-label_margin">Общая информация</div>

      <div className="shared-field-row">
        <div className="shared-field-row-block_width-small">
          <label className="shared-field-label_row" htmlFor={`${fieldName}.checkList`}>
            Наименование
            <ErknmFieldMark fieldName={`${fieldName}.checkList`} />
          </label>
        </div>
        <div className="shared-row-field">
          <SelectInputField
            name={`${fieldName}.checkList`}
            options={checklistsSelectOptions}
            size="medium"
            placeholder="Наименование проверочного листа"
            disabled={isRsnCngPPXXII ? false : watchOnly || !data.isNew}
            onChange={handleTitleSelectChange}
          />
          {data.checkList?.code === NEW_CHECKLIST_SELECT_OPTION_CODE && (
            <TextInputField
              className={styles.NewTitleField}
              name={`${fieldName}.newTitle`}
              placeholder="Введите наименование проверочного листа"
              // validate={makeRequired('Поле не может быть пустым')}
            />
          )}
        </div>
      </div>

      <div className={styles.Questions}>
        <div className={styles.QuestionsHeader}>
          <Checkbox
            id="questions-check-all"
            theme="system"
            onChange={handleCheckboxAllChange}
            checked={isCheckboxAllChecked}
            disabled={watchOnly || !isAddingAvailable}
          />
          <div className={cn('shared-group-label', styles.QuestionsTitle)}>
            Контрольные&nbsp;
            <span className="shared-no-wrap-text">
              вопросы
              <ErknmFieldMark fieldName={questionFieldName} />
              {isAddingAvailable && (
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  variant="link"
                  color="primary"
                  type="button"
                  disabled={watchOnly || !isNewChecklist}
                  onClick={addQuestion}
                />
              )}
            </span>
          </div>
          <KnmChecklistDropdown
            disabled={watchOnly || !isAnswerTypeFieldEnabled}
            questionAnswerTypeFieldName="answer.answerType"
            itemsCheck={itemsCheck}
            data={data}
            fieldName={fieldName}
          />
        </div>

        <p className={styles.QuestionsAddingNotification}>
          {isAddingAvailable && (
            <>
              <Hint text="После сохранения проверочного листа вы не сможете добавить в него контрольные вопросы." />
              <br></br>
            </>
          )}
          <Hint text="Для добавления новых контрольных вопросов создайте новый проверочный лист." />
        </p>

        <FieldArray name={`${fieldName}.questions`} validate={makeRequired(REQUIRED_ERROR)}>
          {({ fields }) => {
            if (!fields.length) {
              return null;
            }

            return (
              <ul className={styles.QuestionsList}>
                {fields.map((name, index) => {
                  if (index > lastVisibleQuestionNumber - 1) {
                    return null;
                  }
                  const question: IKnmErknmFormChecklistQuestion = fields.value[index];

                  return (
                    <li className={styles.Question} key={question.listId}>
                      <div className={styles.QuestionCheckboxBlock}>
                        <Checkbox
                          className={styles.QuestionCheckbox}
                          id={question.listId}
                          theme="system"
                          checked={itemsCheck[question.listId] || false}
                          disabled={watchOnly || !isAddingAvailable}
                          onChange={handleCheckboxChange}
                        />
                      </div>
                      <TextInputField
                        className={styles.QuestionTitle}
                        name={`${name}.title`}
                        placeholder={
                          <>
                            Введите вопрос
                            <ErknmFieldMark fieldName={`${name}.title`} />
                          </>
                        }
                        disabled={watchOnly || !question.isNew}
                      />
                      <SelectInputFieldAutoOptions
                        className={styles.QuestionAnswerTypeSelect}
                        name={`${name}.answer.answerType`}
                        placeholder="Отметка выполнения"
                        optionName={CommonCatalogsNamesEnum.CHECKLIST_ANSWER_TYPES}
                        size="medium"
                        disabled={!isAnswerTypeFieldEnabled || !isKnmEditEnabledByEditors}
                      />
                      <div className={styles.AnswerFieldBlock}>
                        {question.answer?.answerType?.hasText && (
                          <TextInputField
                            name={`${name}.answer.noteAnswer`}
                            placeholder="Введите сведения"
                            disabled={!isNoteAnswerFieldEnabled || !isKnmEditEnabledByEditors}
                          />
                        )}
                      </div>
                      {question.isNew && (
                        <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveQuestion(index)} />
                      )}
                    </li>
                  );
                })}
              </ul>
            );
          }}
        </FieldArray>

        {isNextPageAvailable && (
          <Button type="button" text="Показать еще" color="load" onClick={handleLoadButtonClick} />
        )}
      </div>

      <div className="shared-field-row">
        <label className="shared-field-label_row" htmlFor={`${fieldName}.checkList`}>
          Номер и название из ЕРВК
          <ErknmFieldMark fieldName={`${fieldName}.ervkCheckList`} />
        </label>
      </div>
      <div className={`shared-field-row ${styles.ErvkFieldRow}`}>
        <div className={styles.ErvkFieldRowInner}>
          <SelectInputField
            name={`${fieldName}.ervkCheckList`}
            options={isShow19 ? ervkChecklistsSelectOptions : []}
            size="medium"
            placeholder="Номер и описание проверочного листа из ЕРВК"
            disabled={isErvkCheckListDisabled}
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isKnmStatusWaitingCarriedOut: getKnmIsErpStatusTypeWaitingCarryOut(state),
  knmTypeIdFormValue: getKnmTypeIdFormValue(state),
  knmDataPlanStatus: getPlanListPlanErknmStatusTitle(state),
  ervkChecklistsSelectOptions: getKnmErknmErvkChecklistsSelectOptions(state),
  settings: getAdminFetchSettings(state),
  isKnmEditEnabledByEditors: getKnmIsEditEnabledByEditors(state),
  created: getKnmDataCreated(state),
  isKnmStatusApproving: getKnmIsErknmStatusApproving(state),
  isRsnCngPPXXII: getIsRsnCngPPXXII(state),
});

export const ErknmChecklist = connect(mapStateToProps)(ErknmChecklistWithoutProps);
